import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { TableSkeleton } from "common-fe";
import { useAuth } from "react-oidc-context";
import { filterVisibleClasses } from "score-common";
import { useTranslation } from "i18n-fe";
import AppShell from "./AppShell";
import PageBreadcrumbs from "./PageBreadcrumbs";
import SplitClassChip from "./SplitClassChip";
import { useScoreContext } from "../context/ScoreContext";
import { useClassesForShow } from "../hooks/useClassesForShow";
import { useClassStatuses } from "../hooks/useClassStatuses";
import { useCatalogDefs } from "../hooks/useCatalogDefs";
import { useRingState } from "../hooks/useRingState";
import { scribePositionsFor } from "../util/scribeAssignment";
import {
  classDayKey,
  classEffectiveTime,
  classTestName,
  formatVenueTime,
  nextScheduledClass,
  partitionClassesByDone,
  venueTodayKey,
} from "../util/classDisplay";

// #1280 — keyed by DERIVED_CLASS_STATUSES (score-common). These used to be keyed
// by `Class.status`, a field classSchema never declared and nothing ever wrote,
// so every class fell through to the SCHEDULED default no matter its real state.
// Labels are i18n keys now, not literals.
const STATUS_COLORS = {
  SCHEDULED: { bg: "#e3f2fd", fg: "#1976d2", labelKey: "classes.status.scheduled" },
  IN_PROGRESS: { bg: "#fff3e0", fg: "#f57c00", labelKey: "classes.status.inProgress" },
  COMPLETE: { bg: "#e8f5e9", fg: "#388e3c", labelKey: "classes.status.complete" },
  PLACED: { bg: "#ede7f6", fg: "#5e35b1", labelKey: "classes.status.placed" },
};

export default function ClassList() {
  const navigate = useNavigate();
  const { t } = useTranslation("score");
  const { user } = useAuth();
  const sub = user?.profile?.sub ?? null;
  const { selectedShow } = useScoreContext();
  const { data: classes, loading, refetch, refreshing } = useClassesForShow(selectedShow?.id);
  // #1280 — real, ride-derived class state for the chip. Absent (still loading,
  // or offline with a cold cache) leaves the chip off entirely rather than
  // asserting "Scheduled" about a class we know nothing about.
  // #1557 — the same call now also yields each class's earliest ride time, for
  // the 84-of-189 classes that carry no scheduledTime of their own.
  // #2804 — this hook now skips its revalidate on a warm remount inside its
  // freshness window (see useClassStatuses), so its refetch is threaded into
  // the manual refresh button below: the ONE control that guarantees fresh
  // statuses on demand, same escape hatch #888 gave the class list itself.
  const {
    data: classStatusData,
    settled: classStatusesSettled,
    refetch: refetchStatuses,
    refreshing: statusesRefreshing,
  } = useClassStatuses(selectedShow?.id, classes);
  const classStatuses = classStatusData?.statuses;
  const earliestRideTimes = classStatusData?.earliestRideTimes;
  // card #1664 — table cache preferred over the bundled catalog for the
  // per-class test-name preview below (classTestName).
  const { catalogDefsById } = useCatalogDefs();

  // #1935/#2081 (docs/score/ring-state.md) — "current in ring" read-only
  // indicator (per class card, below). The Start class action itself moved
  // to the ride list page (#2081). Entirely optional and additive: nothing
  // here gates rendering or interaction with the rest of this screen (AC6).
  const ringState = useRingState(selectedShow?.id);

  // #910 — a scribe almost always only cares about TODAY, not every day of a
  // multi-day show. Default ON; toggling off reveals every assigned class
  // regardless of day. "Today" is the VENUE's wall-clock day
  // (America/Los_Angeles, #688) — never the scribe device's own zone — so
  // this compares classDayKey() against venueTodayKey(), both now the SAME
  // real timezone conversion (#1559; they used to be two different
  // derivations — a naive slice for the class, a real conversion for "now"
  // — which is how an evening class silently fell off this view). Computed
  // once per render, not per class, so a long list doesn't re-derive "today"
  // per row.
  const [todayOnly, setTodayOnly] = useState(true);
  const visibleClasses = useMemo(() => {
    // #1117 — a hidden class (hand-hidden, or an emptied RO/ROO split class)
    // drops out of the scribe's class list. Presentation only.
    const all = filterVisibleClasses(classes ?? []);
    if (!todayOnly) return all;
    const todayKey = venueTodayKey();
    return all.filter((c) => classDayKey(c) === todayKey);
  }, [classes, todayOnly]);

  // #1557 — the next class with no scores in it, for the session-header chip.
  // Computed over visibleClasses, not the full list, so it honours the
  // today-only switch: an unscored class left behind on an EARLIER day would
  // otherwise stay "next" forever, which is the same species of permanently
  // wrong chip this card replaced. Order is untouched (visibleClasses is a
  // filter over useClassesForShow's scheduledTime sort), so "earliest" still
  // means earliest.
  const nextClass = useMemo(
    () => nextScheduledClass(visibleClasses, classStatuses),
    [visibleClasses, classStatuses],
  );
  const nextClassTime = nextClass
    ? classEffectiveTime(nextClass, earliestRideTimes)
    : null;

  // #135 / #1702 — split the (already time-ordered) visible list so a
  // scribe never has to scan past finished classes to reach the next one to
  // score: still-to-score classes render first, Completed/Placed classes
  // render below a divider, keeping their existing chronological order in
  // both halves. See partitionClassesByDone for the "done" definition.
  const { active: activeClasses, done: doneClasses } = useMemo(
    () => partitionClassesByDone(visibleClasses, classStatuses),
    [visibleClasses, classStatuses],
  );

  // #2671 — the class list used to paint the moment `loading` (classes only)
  // went false, even though `classStatuses` was still `undefined` at that
  // instant (useClassStatuses is a SEPARATE hook, keyed on the already-
  // resolved class list, so it can't even start until a render after this
  // one). partitionClassesByDone treats an unknown status as not-done (by
  // design, see its own comment), so every class briefly rendered "active"
  // and then jumped to "done" the moment the real statuses landed — the
  // exact visible reflow this card is about.
  // #2678 PATH 1 / #2821 — this used to gate on `classStatusData ===
  // undefined`, which #2162 never resets on a deps change: a class-list
  // change (today-filter toggle, reassignment) keeps the OLD key's settled
  // statuses object in `classStatusData` until the NEW key's own pass
  // resolves, so this read `false` (not loading) while genuinely showing the
  // wrong classes' statuses. `settled` (useCachedResource, common-fe) is a
  // separate flag that DOES reset to `false` on a real deps change (unless
  // the new key already has a warm-cache hit, itself a genuine synchronous
  // settle) without touching `data` — gating on it here closes that gap. On
  // a WARM remount/hit, `settled` is already `true` on the very first render
  // (same as #2671's warm-cache path) and nothing is ever held up. On a
  // genuinely cold cache or a real key change, this stays true until the
  // (possibly network) status fetch resolves for the CURRENT key, holding
  // the list behind the skeleton exactly as AC4 asks for — the
  // reflow-and-fix-it-later option was explicitly rejected.
  const classStatusesLoading = (classes ?? []).length > 0 && !classStatusesSettled;
  const listLoading = loading || classStatusesLoading;
  // #2804 — the manual refresh now drives THREE refetches (classes, statuses,
  // ring state); the button must read as busy until the last of them lands.
  const anyRefreshing = Boolean(refreshing || statusesRefreshing);

  const renderClassCard = (c) => {
    const rawStatus = classStatuses?.[c.id];
    // #1935 — "already current for its ring", independent of how it got
    // there (Start class, or simply the first ride of this class being
    // scored, #1934's SCORED path). #2109 (docs/score/ring-truth.md) — this
    // now goes through useRingState.isCurrentForRing, which itself calls
    // score-common's resolveRingTruth: the ONE source of "in progress" this
    // screen reads.
    const isCurrentInRing = ringState.isCurrentForRing(c.ring, c.id);
    // #2109 — the status chip's IN_PROGRESS label used to come straight off
    // deriveClassStatus (rawStatus), which is only "at least one ride is
    // past PENDING" (score-common/ride-queue.js). That is exactly #2083's
    // 9am-ghost bug: a class the scribe scored earlier and then moved away
    // from keeps its scoresheets open, so rawStatus stays IN_PROGRESS
    // forever even though the ring has long since moved on. The chip now
    // shows "In progress" only when the CENTRAL ring record says this class
    // is the ring's live occupant right now (suppressed once the class is
    // actually COMPLETE/PLACED, same as the ring-current chip below); a
    // rawStatus of IN_PROGRESS that the ring record doesn't confirm renders
    // no chip at all rather than repeating the stale claim (rule 8: no
    // consumer invents a status).
    const displayStatusKey =
      isCurrentInRing && rawStatus !== "COMPLETE" && rawStatus !== "PLACED"
        ? "IN_PROGRESS"
        : rawStatus === "IN_PROGRESS"
          ? null
          : rawStatus;
    const status = STATUS_COLORS[displayStatusKey] ?? null;
    // #627 — the seat this scribe is assigned in THIS class. Normally one;
    // a scribe named at two seats of the same class picks on entry.
    const positions = scribePositionsFor(c, sub);
    // #896 — full test name on the card, so the scribe can see/confirm
    // which test the class rides without opening it. Omitted (not a
    // placeholder) on a Test-of-Choice class with no single test hint.
    const testName = classTestName(c, catalogDefsById);
    return (
      <Card
        key={c.id}
        variant="outlined"
        sx={{
          borderRadius: 3,
          transition: "box-shadow 0.2s, border-color 0.2s",
          "&:hover": {
            borderColor: "primary.main",
            boxShadow: 4,
          },
        }}
      >
        <CardActionArea
          data-testid="class-card"
          onClick={() => navigate(`/classes/${c.id}`)}
          sx={{ p: 0 }}
        >
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box>
                <Typography
                  variant="subtitle1"
                  color="primary"
                  fontWeight={700}
                >
                  {formatVenueTime(c.scheduledTime)}
                </Typography>
                {/* #1761 — a base class and its FVD ride-out/split section
                    carry identical classNumber/className text, so the split
                    card gets the same "Split/RO" chip #883 put on the
                    manual-add picker's rows. The chip sits OUTSIDE the h6:
                    the class label line's textContent is what specs (and
                    scribes scanning the card) read as the name. */}
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="h6" fontWeight={600}>
                    {c.classNumber != null && `${c.classNumber}`.trim() !== ""
                      ? `${c.classNumber}. ${c.className}`
                      : c.className}
                  </Typography>
                  <SplitClassChip klass={c} testId="class-card-split-chip" />
                </Stack>
                {testName && (
                  <Typography
                    variant="body2"
                    data-testid="class-test-name"
                    sx={{ color: "text.secondary" }}
                  >
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {t("rides.test")}:
                    </Box>{" "}
                    {testName}
                  </Typography>
                )}
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ mt: 1, color: "text.secondary" }}
                  divider={<span>•</span>}
                >
                  {c.ring && (
                    <Typography variant="body2">{c.ring}</Typography>
                  )}
                  {positions.length > 0 && (
                    <Typography variant="body2" fontWeight={600}>
                      Position {positions.join(" / ")}
                    </Typography>
                  )}
                </Stack>
              </Box>
              {status && (
                <Chip
                  data-testid="class-status"
                  label={t(status.labelKey)}
                  sx={{
                    bgcolor: status.bg,
                    color: status.fg,
                    fontWeight: 600,
                  }}
                />
              )}
            </Stack>
          </CardContent>
        </CardActionArea>

        {/* #1935/#2081 — "current in ring" indicator, OUTSIDE
            CardActionArea (RideList.jsx's discard-button pattern, so it
            never intercepts a tap into the class). The START CLASS action
            itself moved to the ride list page (#2081, beside Add Ride
            Manually) — this stays behind as a read-only status: a class
            already running in its ring shows the chip, otherwise nothing.
            Suppressed on a COMPLETE/PLACED class (docs/score/ring-state.md)
            same as before, since a finished class can't be "current". */}
        {c.ring && isCurrentInRing && rawStatus !== "COMPLETE" && rawStatus !== "PLACED" && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Chip
              size="small"
              color="success"
              variant="outlined"
              data-testid={`class-ring-current-${c.id}`}
              label={t("classes.ringState.current", { ring: c.ring })}
            />
          </Box>
        )}
      </Card>
    );
  };

  return (
    <AppShell
      showNextClass
      nextClass={nextClass}
      nextClassTime={nextClassTime}
      // #2196 — nextClass is derived from `classes`/`classStatuses`, so the
      // header's "No upcoming class" must stay a loading chip until BOTH of
      // those fetches have actually settled, not flash a confident negative
      // off an empty in-flight list. `classStatusData === undefined` (not a
      // separate `classStatusLoading` flag) is the real signal here: useClassStatuses
      // is keyed on the class list, which starts empty, so its own `loading`
      // flag settles false the instant that empty pass resolves and never
      // resets when the list fills in and the real (slow) status fetch
      // starts (#2162) — see useClassStatuses.js's fetchRemote guard, which
      // now returns `undefined` for exactly this reason.
      // The `(classes ?? []).length > 0` half is what stops that sentinel from
      // becoming a PERMANENT spinner on a show with zero classes: with an empty
      // list BOTH of useClassStatuses' branches return `undefined` by design, so
      // nothing ever settles it and the chip would spin forever, which is the
      // exact defect this card exists to remove. The sentinel is only meaningful
      // once there is at least one class whose status could still be in flight;
      // `loading` already covers the window before the class list itself has
      // settled, so a list that has settled EMPTY correctly reads as a known
      // "No upcoming class". Gating here rather than making the hook's sentinel
      // list-aware keeps the whole rule in one file, next to the `classes` and
      // `classStatuses` that nextClass is actually derived from, instead of
      // resting on useClassesForShow's null-vs-empty-array convention.
      // #2671 — this is now the same `listLoading` the class list itself
      // gates on below, so the header chip and the list settle together.
      nextClassLoading={listLoading}
    >
      <PageBreadcrumbs items={[{ label: t("classes.title") }]} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {t("classes.title")}
        </Typography>
        <FormControlLabel
          sx={{ color: "text.primary", mr: 0 }}
          control={
            <Switch
              data-testid="today-only-switch"
              checked={todayOnly}
              onChange={(e) => setTodayOnly(e.target.checked)}
            />
          }
          label={t("classes.todayOnly")}
        />
      </Stack>

      {/* #888 — mid-show scribe reassignment sits invisible behind the
          IndexedDB cache (useClassesForShow only revalidates on mount/dep
          change) until a full app reload re-mounts the hook. This is the
          one kiosk-appropriate escape hatch: a manual refresh that forces
          useCachedResource's refetch() past the cache. Sticky (top:105 —
          same AppBar(64)+SessionHeader offset ScoringForm's sticky bar
          uses, #647) and pinned top-left of the list so it stays reachable
          while scrolling a long class list, without competing with the
          page title. Own bg/border/shadow (not color="primary" — brand
          primary.main is a near-black grey that all but disappears against
          a dark-mode background, #862) so it reads clearly in both themes. */}
      <Box
        sx={{
          position: "sticky",
          top: 105,
          zIndex: 1100,
          display: "flex",
          justifyContent: "flex-start",
          mb: 1,
        }}
      >
        <Tooltip title={anyRefreshing ? t("classes.refreshing") : t("classes.refresh")}>
          <span>
            <IconButton
              data-testid="refresh-classes-button"
              aria-label={t("classes.refresh")}
              onClick={() => {
                refetch();
                // #2804 — statuses ride a freshness window now (see
                // useClassStatuses), so a warm remount no longer re-pulls
                // them; this button is the control that forces them fresh
                // anyway. Before this card the button refreshed ONLY the
                // class list, leaving the chips (a separate hook) stale.
                refetchStatuses?.();
                // #1935 — the manual refresh also picks up a ring's state
                // changing from elsewhere (another scribe's Start class,
                // Score Admin, or a scoring write), same escape hatch as
                // the class list's own cache.
                ringState.refetch();
              }}
              disabled={anyRefreshing}
              sx={{
                bgcolor: "background.paper",
                color: "text.primary",
                border: 1,
                borderColor: "divider",
                boxShadow: 2,
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <RefreshIcon
                fontSize="small"
                sx={
                  anyRefreshing
                    ? {
                        animation: "classListRefreshSpin 1s linear infinite",
                        "@keyframes classListRefreshSpin": {
                          from: { transform: "rotate(0deg)" },
                          to: { transform: "rotate(360deg)" },
                        },
                      }
                    : undefined
                }
              />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* #714 — card list not yet loaded: shaped skeleton rows, not a bare
          centered spinner, so the screen reads as "content coming" rather
          than "something is broken/blank". #2671 — this now also covers the
          "classes are known but statuses aren't yet" window (listLoading),
          so the card list itself never paints ahead of the completed/active
          split being knowable. On a warm remount classStatuses is warm-
          cached too (see useClassStatuses), so listLoading is already false
          on the very first render and this never shows at all. */}
      {listLoading && (
        <Box sx={{ mt: 2 }}>
          <TableSkeleton rows={5} dense={false} />
        </Box>
      )}

      {!listLoading && (classes ?? []).length === 0 && (
        <Typography color="text.secondary" sx={{ mt: 3 }}>
          {t("classes.empty")}
        </Typography>
      )}

      {/* #910 — the scribe HAS assigned classes, just none scheduled today
          (e.g. viewing the class list the evening before a show, or after the
          venue's last class of the day). Distinct from classes.empty above
          (no assignments at all) so the hint to turn the switch off only
          shows when it would actually reveal something. */}
      {!listLoading && (classes ?? []).length > 0 && visibleClasses.length === 0 && (
        <Typography color="text.secondary" sx={{ mt: 3 }}>
          {t("classes.emptyToday")}
        </Typography>
      )}

      {/* #2671 — gated on listLoading (not just `loading`) so a completed
          class can never paint into this ACTIVE group for even one frame
          while classStatuses is still unknown: the whole list stays behind
          the skeleton above until the partition is knowable, instead of
          rendering everything as "active" and correcting it a moment later. */}
      {!listLoading && (
        <Stack spacing={2} sx={{ mt: 2 }} data-testid="active-classes">
          {activeClasses.map(renderClassCard)}
        </Stack>
      )}

      {/* #135 / #1702 — Completed/Placed classes stay visible and openable
          (a scribe does go back into a placed class to check something),
          just pushed below a labeled divider so they never sit ahead of the
          next class still to score. Only rendered once there IS a done
          class — an all-active list shows no divider at all. */}
      {!listLoading && doneClasses.length > 0 && (
        <>
          <Divider
            data-testid="completed-divider"
            sx={{ mt: activeClasses.length > 0 ? 4 : 2, mb: 2 }}
          >
            <Typography variant="overline" color="text.secondary">
              {t("classes.completedDivider")}
            </Typography>
          </Divider>
          <Stack spacing={2} data-testid="done-classes">
            {doneClasses.map(renderClassCard)}
          </Stack>
        </>
      )}
    </AppShell>
  );
}
